import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { all_routes } from "../../router/all_routes";
import { API_URL } from "../../../environment";
import { canViewTemplatesComErro } from "./access";
import TemplateErrorDetails from "../../utils/TemplateErrorDetails";
import { traduzirErroTemplate } from "../../utils/templateErrors";

// ── Tipos ───────────────────────────────────────────────────────────────────
interface ErroItem {
  ts?: string | null;
  numero_envio?: string | number | null;
  motivo?: string | null;
}

interface ContatoErro {
  id: number;
  nome: string;
  fone: string;
  profilePictureUrl?: string | null;
  idioma?: string | null;
  id_time?: number;
  id_user?: number | null;
  total_erros: number;
  last_error_ts?: string | null;
  last_motivo?: string | null;
  erros?: ErroItem[];
}

interface Msg {
  id: number | string;
  fone: string;
  status: string; // 'send' | 'received' | ...
  mensagem: string;
  timestamp: string;
  read: boolean;
  type: string;
  template?: boolean;
  status_template_wp?: string | null;
  motivo_template_wp?: string | null;
  ins?: number | string | null;
  numero_envio?: string | number | null; // número (instancias.ins_numero) que enviou a mensagem
  id_user?: number | null;
}

const isPublicUrl = (s: string) =>
  /^(https?:\/\/|blob:|\/storage\/v1\/object\/public\/)/i.test(s || "");

// Formata o número de envio (ex.: "554199998888" -> "+55 41 99998888"). Se vier
// vazio ou em formato inesperado, devolve o valor original sem quebrar.
const formatNumeroEnvio = (n?: string | number | null): string | null => {
  if (n === null || n === undefined) return null;
  const raw = String(n).trim();
  if (!raw) return null;
  const d = raw.replace(/\D/g, "");
  if (d.length >= 12 && d.startsWith("55")) {
    const ddd = d.slice(2, 4);
    const resto = d.slice(4);
    return `+55 ${ddd} ${resto}`;
  }
  return raw;
};

const soDigitos = (n?: string | number | null): string =>
  n === null || n === undefined ? "" : String(n).replace(/\D/g, "");

// Filtro de data — mesma semântica do /chat:
// "" = qualquer | "today" | "yesterday" | "7"/"15"/"30" = últimos N dias (calendário) |
// "custom" = intervalo [start 00:00 .. end 23:59:59.999], cada limite opcional.
const matchDateFilter = (
  ts: string | null | undefined,
  choice: string,
  start: string,
  end: string
): boolean => {
  if (!choice) return true;
  if (!ts) return false;
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return false;
  if (choice === "today") {
    return d.toDateString() === new Date().toDateString();
  }
  if (choice === "yesterday") {
    const y = new Date();
    y.setDate(y.getDate() - 1);
    return d.toDateString() === y.toDateString();
  }
  if (choice === "custom") {
    if (start) {
      const s = new Date(`${start}T00:00:00`);
      if (!Number.isNaN(s.getTime()) && d.getTime() < s.getTime()) return false;
    }
    if (end) {
      const e = new Date(`${end}T23:59:59.999`);
      if (!Number.isNaN(e.getTime()) && d.getTime() > e.getTime()) return false;
    }
    return true;
  }
  const n = Number(choice);
  if (!Number.isFinite(n) || n <= 0) return true;
  const cutoff = new Date();
  cutoff.setHours(0, 0, 0, 0);
  cutoff.setDate(cutoff.getDate() - (n - 1));
  return d.getTime() >= cutoff.getTime();
};

// Página "Templates com Erro" — somente visualização (gated p/ id_time 159).
// Coluna esquerda: contatos que tiveram templates com falha de envio.
// Coluna direita: ao selecionar um contato, mostra todas as mensagens (read-only),
// destacando os templates que falharam com o respectivo motivo (motivo_template_wp).
const TemplatesComErro = () => {
  const navigate = useNavigate();
  const autorizado = canViewTemplatesComErro();
  const isDark = useSelector((state: any) => state.themeSetting?.dataTheme) === "dark";
  const idTime = localStorage.getItem("id_cliente") || "";

  const [contatos, setContatos] = useState<ContatoErro[]>([]);
  const [loadingContatos, setLoadingContatos] = useState(true);
  const [erroContatos, setErroContatos] = useState<string | null>(null);

  const [selectedFone, setSelectedFone] = useState<string | null>(null);
  const [mensagens, setMensagens] = useState<Msg[]>([]);
  const [loadingMsgs, setLoadingMsgs] = useState(false);

  // ── Filtros ─────────────────────────────────────────────────────────────
  const [dateChoice, setDateChoice] = useState<string>("");
  const [dateStart, setDateStart] = useState<string>("");
  const [dateEnd, setDateEnd] = useState<string>("");
  const [filtroNumero, setFiltroNumero] = useState<string>(""); // só dígitos do número de envio
  const [filtroMotivo, setFiltroMotivo] = useState<string>(""); // motivo exato

  const firstErrorRef = useRef<HTMLDivElement | null>(null);
  const scrollAreaRef = useRef<HTMLDivElement | null>(null);

  // Bloqueio de rota: quem não é do time 159 é redirecionado, mesmo via URL direta.
  useEffect(() => {
    if (!autorizado) navigate(all_routes.leadsDashboard, { replace: true });
  }, [autorizado, navigate]);

  // Carrega os contatos com templates com erro.
  useEffect(() => {
    if (!autorizado) return;
    let abort = false;
    (async () => {
      setLoadingContatos(true);
      setErroContatos(null);
      try {
        const resp = await fetch(
          `${API_URL}/contatos_templates_com_erro?id_time=${encodeURIComponent(idTime)}`
        );
        const data = await resp.json();
        if (!abort) setContatos(Array.isArray(data) ? data : []);
      } catch (e) {
        if (!abort) setErroContatos("Não foi possível carregar os contatos.");
      } finally {
        if (!abort) setLoadingContatos(false);
      }
    })();
    return () => {
      abort = true;
    };
  }, [autorizado, idTime]);

  // Carrega as mensagens do contato selecionado.
  const abrirContato = async (fone: string) => {
    setSelectedFone(fone);
    setLoadingMsgs(true);
    setMensagens([]);
    try {
      const resp = await fetch(
        `${API_URL}/mensagens_contato_erro?id_time=${encodeURIComponent(
          idTime
        )}&fone=${encodeURIComponent(fone)}`
      );
      const data = await resp.json();
      setMensagens(Array.isArray(data) ? data : []);
    } catch (e) {
      setMensagens([]);
    } finally {
      setLoadingMsgs(false);
    }
  };

  // Após renderizar as mensagens, rola até o primeiro template com erro (ou ao fim).
  useEffect(() => {
    if (loadingMsgs) return;
    const t = setTimeout(() => {
      if (firstErrorRef.current) {
        firstErrorRef.current.scrollIntoView({ behavior: "auto", block: "center" });
      } else if (scrollAreaRef.current) {
        scrollAreaRef.current.scrollTop = scrollAreaRef.current.scrollHeight;
      }
    }, 60);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mensagens, loadingMsgs, dateChoice, dateStart, dateEnd, filtroNumero, filtroMotivo]);

  // Opções dos selects, derivadas dos próprios dados de erro.
  // Números de envio distintos (devem ser 2) e motivos de erro distintos.
  const numerosDisponiveis = useMemo(() => {
    const map = new Map<string, string>(); // dígitos -> rótulo formatado
    contatos.forEach((c) =>
      (c.erros || []).forEach((e) => {
        const dig = soDigitos(e.numero_envio);
        if (dig) map.set(dig, formatNumeroEnvio(e.numero_envio) || dig);
      })
    );
    return Array.from(map.entries()).map(([dig, label]) => ({ dig, label }));
  }, [contatos]);

  const motivosDisponiveis = useMemo(() => {
    const set = new Set<string>();
    contatos.forEach((c) =>
      (c.erros || []).forEach((e) => {
        const m = (e.motivo || "").trim();
        if (m) set.add(m);
      })
    );
    return Array.from(set);
  }, [contatos]);

  // Um erro casa os filtros se atende data + número + motivo (todos opcionais).
  const erroMatchFiltros = (e: ErroItem): boolean => {
    if (!matchDateFilter(e.ts, dateChoice, dateStart, dateEnd)) return false;
    if (filtroNumero && soDigitos(e.numero_envio) !== filtroNumero) return false;
    if (filtroMotivo && (e.motivo || "").trim() !== filtroMotivo) return false;
    return true;
  };

  const filtrosAtivos = !!(dateChoice || filtroNumero || filtroMotivo);

  // Contato aparece se tem ao menos 1 erro que casa os filtros; a contagem
  // exibida passa a refletir só os erros filtrados.
  const contatosFiltrados = useMemo(() => {
    if (!filtrosAtivos) return contatos.map((c) => ({ c, count: c.total_erros }));
    return contatos
      .map((c) => ({ c, count: (c.erros || []).filter(erroMatchFiltros).length }))
      .filter((x) => x.count > 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contatos, dateChoice, dateStart, dateEnd, filtroNumero, filtroMotivo]);

  const limparFiltros = () => {
    setDateChoice("");
    setDateStart("");
    setDateEnd("");
    setFiltroNumero("");
    setFiltroMotivo("");
  };

  const contatoSel = useMemo(
    () => contatos.find((c) => c.fone === selectedFone) || null,
    [contatos, selectedFone]
  );

  if (!autorizado) return null;

  // ── Estilos derivados do tema ──────────────────────────────────────────────
  const bgPanel = isDark ? "#111b21" : "#f7f8fa";
  const bgCard = isDark ? "#1f2c34" : "#ffffff";
  const border = isDark ? "#2a3942" : "#e6e9ef";
  const textMain = isDark ? "#e9edef" : "#1a1a2e";
  const textMuted = isDark ? "#8696a0" : "#6b7280";

  const renderConteudoMsg = (m: Msg) => {
    switch (m.type) {
      case "imagem":
      case "image": {
        const src = isPublicUrl(m.mensagem) ? m.mensagem : `data:image/png;base64,${m.mensagem}`;
        return <img src={src} alt="Imagem" style={{ maxWidth: "100%", borderRadius: 8 }} />;
      }
      case "video": {
        const src = isPublicUrl(m.mensagem) ? m.mensagem : `data:video/mp4;base64,${m.mensagem}`;
        return <video controls src={src} style={{ maxWidth: "100%", borderRadius: 8 }} />;
      }
      case "audio": {
        const src = isPublicUrl(m.mensagem) ? m.mensagem : `data:audio/mp3;base64,${m.mensagem}`;
        return <audio controls src={src} style={{ width: "100%", minWidth: 240 }} />;
      }
      case "document": {
        const href = isPublicUrl(m.mensagem)
          ? m.mensagem
          : `data:application/octet-stream;base64,${m.mensagem}`;
        const fileName = (m.mensagem.split("/").pop() || "documento").replace(/\?.*$/, "");
        return (
          <a href={href} download={fileName} target="_blank" rel="noopener noreferrer">
            📎 {fileName}
          </a>
        );
      }
      default:
        return <span style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{m.mensagem}</span>;
    }
  };

  let firstErrorAssigned = false;

  return (
    <div className="page-wrapper">
      <div className="content">
        {/* Cabeçalho */}
        <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
          <div>
            <h4 className="mb-1" style={{ color: textMain }}>
              <i className="ti ti-alert-triangle me-2" style={{ color: "#ef4444" }} />
              Templates com Erro
            </h4>
            <p className="mb-0" style={{ color: textMuted, fontSize: 13 }}>
              Contatos que tiveram templates com falha de envio. Somente visualização.
            </p>
          </div>
          <span
            className="badge"
            style={{
              background: isDark ? "#2a1416" : "#fff5f5",
              color: "#ef4444",
              border: "1px solid #ef4444",
              fontSize: 13,
              padding: "8px 12px",
            }}
          >
            {contatosFiltrados.length} contato(s)
          </span>
        </div>

        {/* Barra de filtros */}
        <div
          className="d-flex align-items-end flex-wrap gap-2 mb-3 p-2"
          style={{ background: bgCard, border: `1px solid ${border}`, borderRadius: 8 }}
        >
          {/* Data — igual ao /chat */}
          <div style={{ minWidth: 190 }}>
            <label style={{ fontSize: 11, color: textMuted, fontWeight: 600 }}>Data do erro</label>
            <select
              className="form-select form-select-sm"
              value={dateChoice}
              onChange={(e) => setDateChoice(e.target.value)}
              title="Filtra os erros pela data"
            >
              <option value="">— Qualquer data —</option>
              <option value="today">Hoje</option>
              <option value="yesterday">Ontem</option>
              <option value="7">Últimos 7 dias</option>
              <option value="15">Últimos 15 dias</option>
              <option value="30">Últimos 30 dias</option>
              <option value="custom">Intervalo personalizado…</option>
            </select>
          </div>

          {dateChoice === "custom" && (
            <div className="d-flex gap-1" style={{ minWidth: 230 }}>
              <div>
                <label style={{ fontSize: 11, color: textMuted, fontWeight: 600 }}>De</label>
                <input
                  type="date"
                  className="form-control form-control-sm"
                  value={dateStart}
                  max={dateEnd || undefined}
                  onChange={(e) => setDateStart(e.target.value)}
                  aria-label="Data inicial"
                />
              </div>
              <div>
                <label style={{ fontSize: 11, color: textMuted, fontWeight: 600 }}>Até</label>
                <input
                  type="date"
                  className="form-control form-control-sm"
                  value={dateEnd}
                  min={dateStart || undefined}
                  onChange={(e) => setDateEnd(e.target.value)}
                  aria-label="Data final"
                />
              </div>
            </div>
          )}

          {/* Número de envio (2) */}
          <div style={{ minWidth: 190 }}>
            <label style={{ fontSize: 11, color: textMuted, fontWeight: 600 }}>Número de envio</label>
            <select
              className="form-select form-select-sm"
              value={filtroNumero}
              onChange={(e) => setFiltroNumero(e.target.value)}
              title="Filtra pelo número que enviou o template"
            >
              <option value="">— Todos os números —</option>
              {numerosDisponiveis.map((n) => (
                <option key={n.dig} value={n.dig}>
                  {n.label}
                </option>
              ))}
            </select>
          </div>

          {/* Tipo de erro */}
          <div style={{ minWidth: 220, flex: 1 }}>
            <label style={{ fontSize: 11, color: textMuted, fontWeight: 600 }}>Erro do template</label>
            <select
              className="form-select form-select-sm"
              value={filtroMotivo}
              onChange={(e) => setFiltroMotivo(e.target.value)}
              title="Filtra pelo motivo do erro"
            >
              <option value="">— Todos os erros —</option>
              {motivosDisponiveis.map((m) => (
                <option key={m} value={m}>
                  {traduzirErroTemplate(m).titulo}
                </option>
              ))}
            </select>
          </div>

          {filtrosAtivos && (
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary"
              onClick={limparFiltros}
              title="Limpar filtros"
            >
              <i className="ti ti-x me-1" />
              Limpar
            </button>
          )}
        </div>

        {/* Corpo: lista + mensagens */}
        <div
          style={{
            display: "flex",
            gap: 0,
            height: "calc(100vh - 170px)",
            border: `1px solid ${border}`,
            borderRadius: 10,
            overflow: "hidden",
            background: bgPanel,
          }}
        >
          {/* Coluna esquerda — lista de contatos */}
          <div
            style={{
              width: 360,
              minWidth: 280,
              borderRight: `1px solid ${border}`,
              display: "flex",
              flexDirection: "column",
              background: bgCard,
            }}
          >
            <div
              style={{
                padding: "10px 14px",
                borderBottom: `1px solid ${border}`,
                fontWeight: 600,
                color: textMain,
                fontSize: 14,
              }}
            >
              Contatos com falha
            </div>
            <div style={{ overflowY: "auto", flex: 1 }}>
              {loadingContatos ? (
                <div style={{ padding: 20, color: textMuted }}>Carregando…</div>
              ) : erroContatos ? (
                <div style={{ padding: 20, color: "#ef4444" }}>{erroContatos}</div>
              ) : contatos.length === 0 ? (
                <div style={{ padding: 20, color: textMuted }}>
                  Nenhum contato com template com erro.
                </div>
              ) : contatosFiltrados.length === 0 ? (
                <div style={{ padding: 20, color: textMuted }}>
                  Nenhum contato corresponde aos filtros.
                </div>
              ) : (
                contatosFiltrados.map(({ c, count }) => {
                  const ativo = c.fone === selectedFone;
                  return (
                    <button
                      key={`${c.id}-${c.fone}`}
                      onClick={() => abrirContato(c.fone)}
                      style={{
                        width: "100%",
                        textAlign: "left",
                        border: "none",
                        borderBottom: `1px solid ${border}`,
                        background: ativo ? (isDark ? "#2a3942" : "#eef6f8") : "transparent",
                        padding: "10px 14px",
                        cursor: "pointer",
                        display: "flex",
                        gap: 10,
                        alignItems: "center",
                      }}
                    >
                      <div
                        style={{
                          width: 42,
                          height: 42,
                          borderRadius: "50%",
                          flex: "0 0 auto",
                          background: isDark ? "#374955" : "#d7e9ee",
                          color: isDark ? "#e9edef" : "#007e94",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontWeight: 700,
                          overflow: "hidden",
                        }}
                      >
                        {c.profilePictureUrl ? (
                          <img
                            src={c.profilePictureUrl}
                            alt=""
                            style={{ width: "100%", height: "100%", objectFit: "cover" }}
                          />
                        ) : (
                          (c.nome || c.fone || "?").trim().charAt(0).toUpperCase()
                        )}
                      </div>
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <div
                          style={{
                            fontWeight: 600,
                            color: textMain,
                            fontSize: 14,
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {c.nome || c.fone}
                        </div>
                        <div
                          style={{
                            color: textMuted,
                            fontSize: 12,
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {traduzirErroTemplate(c.last_motivo).titulo}
                        </div>
                      </div>
                      <span
                        title={
                          filtrosAtivos
                            ? `${count} de ${c.total_erros} erro(s) no filtro`
                            : `${count} erro(s)`
                        }
                        style={{
                          flex: "0 0 auto",
                          background: "#ef4444",
                          color: "#fff",
                          borderRadius: 10,
                          fontSize: 11,
                          fontWeight: 700,
                          padding: "2px 7px",
                          minWidth: 22,
                          textAlign: "center",
                        }}
                      >
                        {count}
                      </span>
                    </button>
                  );
                })
              )}
            </div>
          </div>

          {/* Coluna direita — mensagens (read-only) */}
          <div style={{ flex: 1, display: "flex", flexDirection: "column", minWidth: 0 }}>
            {!selectedFone ? (
              <div
                style={{
                  flex: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: textMuted,
                  flexDirection: "column",
                  gap: 8,
                }}
              >
                <i className="ti ti-message-2" style={{ fontSize: 40, opacity: 0.5 }} />
                Selecione um contato para ver as mensagens.
              </div>
            ) : (
              <>
                {/* Header do contato */}
                <div
                  style={{
                    padding: "10px 16px",
                    borderBottom: `1px solid ${border}`,
                    background: bgCard,
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                  }}
                >
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontWeight: 600, color: textMain }}>
                      {contatoSel?.nome || selectedFone}
                    </div>
                    <div style={{ color: textMuted, fontSize: 12 }}>{selectedFone}</div>
                  </div>
                </div>

                {/* Lista de mensagens */}
                <div ref={scrollAreaRef} style={{ flex: 1, overflowY: "auto", padding: "16px" }}>
                  {loadingMsgs ? (
                    <div style={{ color: textMuted }}>Carregando mensagens…</div>
                  ) : mensagens.length === 0 ? (
                    <div style={{ color: textMuted }}>Sem mensagens para este contato.</div>
                  ) : (
                    mensagens.map((m) => {
                      const enviado = m.status === "send";
                      const failed =
                        m.template === true &&
                        (m.status_template_wp || "").toLowerCase() === "failed";
                      // Auto-scroll: 1º erro que casa os filtros ativos (ou 1º erro qualquer).
                      const casaFiltros =
                        !filtrosAtivos ||
                        erroMatchFiltros({
                          ts: m.timestamp,
                          numero_envio: m.numero_envio,
                          motivo: m.motivo_template_wp,
                        });
                      let assignRef = false;
                      if (failed && casaFiltros && !firstErrorAssigned) {
                        firstErrorAssigned = true;
                        assignRef = true;
                      }
                      return (
                        <div
                          key={m.id}
                          ref={assignRef ? firstErrorRef : undefined}
                          style={{
                            display: "flex",
                            justifyContent: enviado ? "flex-end" : "flex-start",
                            marginBottom: 8,
                          }}
                        >
                          <div
                            style={{
                              maxWidth: "72%",
                              background: failed
                                ? isDark
                                  ? "#3a1d1f"
                                  : "#fff1f1"
                                : enviado
                                ? isDark
                                  ? "#005c4b"
                                  : "#dcf8c6"
                                : bgCard,
                              color: textMain,
                              border: failed ? "1px solid #ef4444" : `1px solid ${border}`,
                              borderRadius: enviado ? "12px 4px 12px 12px" : "4px 12px 12px 12px",
                              padding: "8px 12px",
                              fontSize: 14,
                              lineHeight: 1.5,
                            }}
                          >
                            {m.template === true && (
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  flexWrap: "wrap",
                                  gap: 6,
                                  fontSize: 10,
                                  fontWeight: 700,
                                  letterSpacing: 0.3,
                                  textTransform: "uppercase",
                                  color: textMuted,
                                  marginBottom: 3,
                                }}
                              >
                                <span>Template</span>
                                {formatNumeroEnvio(m.numero_envio) && (
                                  <span
                                    title="Número que enviou este template"
                                    style={{
                                      display: "inline-flex",
                                      alignItems: "center",
                                      gap: 3,
                                      textTransform: "none",
                                      fontWeight: 600,
                                      letterSpacing: 0,
                                      background: isDark ? "#0b3a30" : "#e8f5ef",
                                      color: isDark ? "#7bd6bb" : "#0a7d5a",
                                      borderRadius: 6,
                                      padding: "1px 6px",
                                    }}
                                  >
                                    <i className="ti ti-brand-whatsapp" style={{ fontSize: 12 }} />
                                    {formatNumeroEnvio(m.numero_envio)}
                                  </span>
                                )}
                              </div>
                            )}

                            {renderConteudoMsg(m)}

                            {/* Faixa do motivo do erro (traduzido) */}
                            {failed && (
                              <div
                                style={{
                                  marginTop: 8,
                                  background: isDark ? "#2a1416" : "#fff5f5",
                                  border: "1px solid #ef4444",
                                  borderRadius: 6,
                                  padding: "8px 10px",
                                  color: isDark ? "#fecaca" : "#7f1d1d",
                                }}
                              >
                                <TemplateErrorDetails
                                  motivo={m.motivo_template_wp}
                                  isDark={isDark}
                                  fontSize={11.5}
                                />
                                {formatNumeroEnvio(m.numero_envio) && (
                                  <div style={{ marginTop: 6, opacity: 0.85, fontSize: 11.5 }}>
                                    Enviado pelo número:{" "}
                                    <strong>{formatNumeroEnvio(m.numero_envio)}</strong>
                                  </div>
                                )}
                              </div>
                            )}

                            <div
                              style={{
                                marginTop: 4,
                                fontSize: 10.5,
                                color: textMuted,
                                textAlign: "right",
                              }}
                            >
                              {m.timestamp
                                ? new Date(m.timestamp).toLocaleString("pt-BR", {
                                    day: "2-digit",
                                    month: "2-digit",
                                    hour: "2-digit",
                                    minute: "2-digit",
                                  })
                                : ""}
                            </div>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TemplatesComErro;
